/**
 * SA-947 (spec P12): link a WhatsApp group source's desk and bind its group.
 * The step shown is the pure `connectPhase` over the status read, polled every
 * 3 s while there is something to wait for (`statusInterval`). Every refusal
 * is worded by `refusalText`; the words live in `groupSourceText`.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Alert, AlertDescription, Button, Input, Modal, ModalBody, ModalContent, ModalDescription,
  ModalFooter, ModalHeader, ModalTitle, Spinner,
} from '@/components/ui'
import {
  MAILBOXES_KEY, useBindGroup, useConnectMailbox, useRequestPairingCode, useUnbindGroup,
  useWahaGroups, useWahaQr, useWahaStatus,
} from '@/hooks/useMailboxes'
import type { Mailbox } from '@/services/mailboxes'
import { refusalText } from './connectErrorText'
import { LINK_STEPS, boundLine, pairingCodeText, participantsText } from './groupSourceText'
import { connectPhase, statusInterval } from './wahaConnectModel'

interface WahaConnectDialogProps {
  mailbox: Mailbox
  open: boolean
  onOpenChange: (open: boolean) => void
}

const TRY_AGAIN = 'The request did not complete. Try again.'

/** The QR as an object URL, revoked when it is replaced or the dialog goes. */
function QrImage({ blob }: { blob: Blob | undefined }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob) {
      setUrl(null)
      return
    }
    const created = URL.createObjectURL(blob)
    setUrl(created)
    return () => URL.revokeObjectURL(created)
  }, [blob])
  return url
    ? <img src={url} alt="QR code to link the desk's phone" className="h-48 w-48 rounded-md border border-border" />
    : <Spinner />
}

export function WahaConnectDialog({ mailbox, open, onOpenChange }: WahaConnectDialogProps) {
  const qc = useQueryClient()
  const status = useWahaStatus(mailbox.id, open, statusInterval)
  const read = status.data
  const phase = connectPhase(read)
  const qr = useWahaQr(mailbox.id, open && phase === 'link')
  const groups = useWahaGroups(mailbox.id, open && phase === 'choose')
  const codeMutation = useRequestPairingCode()
  const bindMutation = useBindGroup()
  const unbindMutation = useUnbindGroup()
  const connectMutation = useConnectMailbox()
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState<string | null>(null)
  const [chosen, setChosen] = useState('')
  const [error, setError] = useState<string | null>(null)

  // A read that failed with nothing to show is said; a failed background
  // refetch keeps the last step (the readState rule).
  const readError = status.isError && read === undefined ? refusalText(status.error) ?? TRY_AGAIN : null

  const close = (next: boolean) => {
    // The status read moves the row; the list shows it once the dialog closes.
    if (!next) void qc.invalidateQueries({ queryKey: MAILBOXES_KEY })
    onOpenChange(next)
  }

  const run = async (action: () => Promise<void>) => {
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(refusalText(err) ?? TRY_AGAIN)
    }
  }

  const getCode = () => run(async () => {
    const answer = await codeMutation.mutateAsync({ id: mailbox.id, phoneNumber: phone.trim() })
    setCode(answer.code)
  })
  const bind = () => run(async () => { await bindMutation.mutateAsync({ id: mailbox.id, groupId: chosen }) })
  const changeGroup = () => run(async () => {
    await unbindMutation.mutateAsync(mailbox.id)
    setChosen('')
  })
  // A 502 on connect also lands here: connecting again heals the session's key.
  const reconnect = () => run(async () => {
    setCode(null)
    await connectMutation.mutateAsync({ id: mailbox.id })
  })

  const ended = (sentence: string) => (
    <div className="space-y-3">
      <p className="text-sm text-foreground">{sentence}</p>
      <Button variant="outline" size="sm" onClick={reconnect} disabled={connectMutation.isPending}>
        Connect again
      </Button>
    </div>
  )

  let body: ReactNode
  if (readError) {
    body = ended(readError)
  } else if (phase === 'failed') {
    body = ended('WhatsApp ended the link. Connect again to link the desk.')
  } else if (phase === 'stopped') {
    body = <p className="text-sm text-foreground">The session is stopped. Resume the source to link it.</p>
  } else if (phase === 'link') {
    body = (
      <div className="space-y-4">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground">
          {LINK_STEPS.map((step) => <li key={step}>{step}</li>)}
        </ol>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Input
              id={`waha-phone-${mailbox.id}`}
              label="Desk's phone number"
              value={phone}
              placeholder="+44 7700 900123"
              onChange={(e) => setPhone(e.target.value)}
            />
            <Button size="sm" onClick={getCode} disabled={!phone.trim() || codeMutation.isPending}>
              Get pairing code
            </Button>
            {code && (
              <p aria-live="polite" className="font-mono text-2xl tracking-widest text-foreground">
                {pairingCodeText(code)}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <QrImage blob={qr.data} />
            {qr.isError && !qr.data && (
              <p className="text-xs text-destructive">{refusalText(qr.error) ?? 'The QR code could not be read.'}</p>
            )}
          </div>
        </div>
      </div>
    )
  } else if (phase === 'choose') {
    const list = groups.data ?? []
    body = (
      <div className="space-y-3">
        {groups.isLoading && <Spinner />}
        {groups.isError && !groups.data && (
          <p className="text-sm text-destructive">{refusalText(groups.error) ?? TRY_AGAIN}</p>
        )}
        {groups.data && list.length === 0 && (
          <p className="text-sm text-foreground">The desk is in no group yet. Add it to the group from a phone, then refresh.</p>
        )}
        {list.length > 0 && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">Which group does this source listen to?</legend>
            {list.map((group) => (
              <label key={group.id} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={`waha-group-${mailbox.id}`}
                  value={group.id}
                  checked={chosen === group.id}
                  onChange={() => setChosen(group.id)}
                />
                <span>{`${group.subject} · ${participantsText(group.participant_count)}`}</span>
              </label>
            ))}
          </fieldset>
        )}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => void groups.refetch()}>Refresh</Button>
          <Button size="sm" onClick={bind} disabled={!chosen || bindMutation.isPending}>Bind</Button>
        </div>
      </div>
    )
  } else if (phase === 'bound' && read) {
    body = (
      <div className="space-y-3">
        <p className="text-sm text-foreground">{boundLine(read)}</p>
        <Button variant="ghost" size="sm" onClick={changeGroup} disabled={unbindMutation.isPending}>
          Change group
        </Button>
      </div>
    )
  } else {
    body = (
      <p className="flex items-center gap-2 text-sm text-foreground">
        <Spinner size="sm" /> Starting the WhatsApp session…
      </p>
    )
  }

  return (
    <Modal open={open} onOpenChange={close}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>Link the desk&apos;s WhatsApp</ModalTitle>
          <ModalDescription>
            {mailbox.display_name}: link the desk&apos;s phone, then choose the group to listen to.
          </ModalDescription>
        </ModalHeader>
        <ModalBody className="space-y-4">
          {body}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant={phase === 'bound' ? undefined : 'ghost'} onClick={() => close(false)}>
            {phase === 'bound' ? 'Done' : 'Close'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
